import { typographyCss, colors } from '@creditornot/cb-ingredients';
import React, { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import ErrorToastContent from 'common/components/ErrorToastContent';
import { localize } from 'common/utils';
import CategoryChangeErrorToast from 'modules/menu/categories/components/CategoryChangeErrorToast';
import CategoryChangeSuccessToast from 'modules/menu/categories/components/CategoryChangeSuccessToast';
import { batchChangeCategory, updateOfferBindingsOrder } from 'modules/shared-menu/api';
import { BatchCategoryChangePayload, SelectionCategory } from 'modules/shared-menu/types';

import useOfferCategoryChange from './useOfferCategoryChange';
import { useSelectionData } from '../../../../../hooks/useSelectionData';
import { flattenCategories, getParentCategory } from '../../../utils';

type Props = {
  onClearSelection: () => void;
  categoryId: string | null;
  menuEditorRootUrl: string;
};

const SUCCESS_TIMEOUT = 10 * 1000;

export const buildCategoryLink = (baseUrl: string, categoryId: string, categories: SelectionCategory[]) => {
  const category = flattenCategories(categories).find(category => category.id === categoryId);
  if (!category) {
    return baseUrl;
  }
  const parentCategory = getParentCategory(category.id, categories);
  if (parentCategory) {
    return `${baseUrl}/${parentCategory.id}/subcategories/${category.id}`;
  }
  return `${baseUrl}/${category.id}`;
};

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`;

const CategoryLink = styled(Link)`
  ${typographyCss.Body3};
  font-weight: 500;
  margin-block-start: 8px;
  margin-inline-end: 16px;
  color: ${colors.wolt100};

  &:hover {
    color: ${colors.wolt92};
  }
`;

const SuccessToast = ({ categoryName, link }: { categoryName?: string; link: string }) => {
  return (
    <div>
      <FormattedMessage id="menu.bulkActions.categoryChange.successDescription" />
      <Actions>
        {categoryName && (
          <CategoryLink to={link}>
            <FormattedMessage id="menu.goToCategory" values={{ category: categoryName }} />
          </CategoryLink>
        )}
      </Actions>
    </div>
  );
};

export const useDndMethods = ({ onClearSelection, categoryId, menuEditorRootUrl }: Props) => {
  const { selection, categories, offers, updateSelectionData } = useSelectionData();

  const intl = useIntl();
  const changeItemCategory = useOfferCategoryChange();

  const categoryOfferIdsRecord = useMemo(
    () =>
      flattenCategories(categories).reduce(
        (obj, current) => ({
          [current.id]: (current.offer_bindings ?? []).map(({ offer_id }) => offer_id),
          ...obj,
        }),
        {} as Record<string, string[]>,
      ),
    [categories],
  );

  const handleBatchMoveOffersToCategory = useCallback(
    async (overId: string | null, selectedIds: Set<string>) => {
      const ids = [...selectedIds];
      const isDraggingToSameCategory = overId ? ids.every(id => categoryOfferIdsRecord[overId]?.includes(id)) : false;
      if (isDraggingToSameCategory) {
        return;
      }
      const batchCategoryChangePayload: BatchCategoryChangePayload = {
        offer_ids: ids,
        destination_category_id: overId,
      };
      try {
        const updatedSelection = await batchChangeCategory(selection.id, batchCategoryChangePayload);
        updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });

        const category = flattenCategories(updatedSelection.categories).find(category => category.id === overId);
        const categoryName = category
          ? localize(category.name, selection.primary_language)
          : intl.formatMessage({
              id: 'menu.nonCategorised',
            });
        const link = !overId
          ? `${menuEditorRootUrl}/no-category`
          : buildCategoryLink(menuEditorRootUrl, overId, updatedSelection.categories);

        toast.success(<SuccessToast link={link} categoryName={categoryName} />);
      } catch (error) {
        toast.error(<ErrorToastContent error={error} />);
      }
    },
    [categoryOfferIdsRecord, menuEditorRootUrl, selection, updateSelectionData, intl],
  );

  const handleMoveOfferToCategory = useCallback(
    async (activeId: string, overId: null | string) => {
      const offer = offers.find(offer => offer.id === activeId);
      const offerName = offer ? localize(offer.name, selection.primary_language) : activeId;
      const category = flattenCategories(categories).find(category => category.id === overId);
      const categoryName = category
        ? localize(category.name, selection.primary_language)
        : intl.formatMessage({
            id: 'menu.nonCategorised',
          });
      const link = !overId
        ? `${menuEditorRootUrl}/no-category`
        : buildCategoryLink(menuEditorRootUrl, overId, categories);

      try {
        const undoItemCategoryChange = await changeItemCategory(overId, activeId);
        toast.success(
          <CategoryChangeSuccessToast
            categoryName={categoryName}
            itemName={offerName}
            link={link}
            onUndo={() => {
              toast.dismiss();
              toast.promise(undoItemCategoryChange(), { loading: '', success: '', error: error => error.message });
            }}
          />,
          {
            duration: SUCCESS_TIMEOUT,
          },
        );
      } catch (error) {
        toast.error(<CategoryChangeErrorToast itemName={offerName} categoryName={categoryName} />, {
          duration: Infinity,
        });
      }
    },
    [changeItemCategory, intl, selection, offers, categories, menuEditorRootUrl],
  );

  const handleUpdateOfferBindingOrder = useCallback(
    async (listIds: string[]) => {
      if (categoryId) {
        try {
          const updatedMenu = await updateOfferBindingsOrder(selection.id, categoryId, listIds);
          updateSelectionData({ actionType: 'updateSelection', payload: updatedMenu });
        } catch (error) {
          toast.error(error.message, { duration: Infinity });
        }
      }
    },
    [categoryId, selection.id, updateSelectionData],
  );

  const handleMoveOffersToCategory = useCallback(
    async (activeId: string, overId: string | null, selectedIds?: Set<string>): Promise<void> => {
      if (!selectedIds) {
        await handleMoveOfferToCategory(activeId, overId);
      }
      if (selectedIds) {
        await handleBatchMoveOffersToCategory(overId, selectedIds);
        onClearSelection();
      }
    },
    [onClearSelection, handleMoveOfferToCategory, handleBatchMoveOffersToCategory],
  );

  return {
    onUpdateOfferBindingOrder: categoryId ? handleUpdateOfferBindingOrder : undefined,
    onMoveOffersToCategory: handleMoveOffersToCategory,
  };
};
